import { lazy, Suspense, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../core/store';
import { getVesselLastVoyagesRequestAction } from '../../../main-map/slices/vesselLastVoyagesSlice';
import { getVesselLastStoppageEventsRequestAction } from '../../../main-map/slices/vesselLastStoppageEventsSlice';
import { getVesselLastPortCallsRequestAction } from '../../../main-map/slices/vesselLastPortCallsSlice';
import { Layer, Source } from 'react-map-gl';
import {
	lineLayerStyle,
	pointArrowLayerStyleForVoyage,
	portCallsLayerStyle,
	stoppagesLayerStyle,
} from '../../../../shared/components/globe-map/constants/mapLayers';
import { fixAntimeridian } from '../../../../utils/mapHelpers';
import { getVesselVoyageDailySignalsRequestAction } from '../../../main-map/slices/vesselVoyageDailySignalsSlice';
import LoadingSpinner from '../../../../shared/components/loading-spinner/LoadingSpinner';
import { selectVesselLastSignalInfoByImoNumber } from '../../../main-map/mapSelectors';
import { CsvDownloadButton } from '../../../../shared/components/charts/DownloadButton';
import { kpisActions } from '../../kpiSlice';
import { selectVesselVoyagesHourlyTracks } from '../../kpiSelectors';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../date-range/slices/dateRangeSlice';
import { formatValueAsString } from '../../../../utils/helpers';

const GlobeMap = lazy(() => import('../../../../shared/components/globe-map/GlobeMap'));

interface IOperationalPhaseMapProps {
	imoNumber: number;
}

const OperationalPhaseMap: React.FC<IOperationalPhaseMapProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const formatOptions: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 2 };

	const { vesselLastVoyagesData } = useAppSelector((state: RootState) => state.vesselLastVoyagesSlice);
	const { vesselLastStoppageEventsData } = useAppSelector((state: RootState) => state.vesselLastStoppageEventsSlice);
	const { vesselLastPortCallsData } = useAppSelector((state: RootState) => state.vesselLastPortCallsSlice);
	const { vesselVoyageDailySignalsData } = useAppSelector((state: RootState) => state.vesselVoyageDailySignalsSlice);

	const vesselLastSignalInfo = useAppSelector((state: RootState) =>
		selectVesselLastSignalInfoByImoNumber(state, props.imoNumber!),
	);

	const vesselVoyagesHourlyTracks = useAppSelector(selectVesselVoyagesHourlyTracks);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	useEffect(() => {
		if (props.imoNumber && analysisDateRange && analysisDateRange.dateRange) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString(); 
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();

			dispatch(getVesselLastVoyagesRequestAction({ imoNumbers: [props.imoNumber], startDate, endDate }));
			dispatch(getVesselLastStoppageEventsRequestAction({ imoNumber: props.imoNumber, startDate, endDate }));
			dispatch(getVesselLastPortCallsRequestAction({ imoNumber: props.imoNumber, startDate, endDate }));
			dispatch(getVesselVoyageDailySignalsRequestAction({ imoNumbers: [props.imoNumber], startDate, endDate }));

			if (!vesselVoyagesHourlyTracks || vesselVoyagesHourlyTracks.imoNumber !== props.imoNumber)
				dispatch(kpisActions.getVesselVoyagesHourlyTracksRequestAction({ imoNumber: props.imoNumber, startDate, endDate }));
		}
	}, [dispatch, props.imoNumber, analysisDateRange]);

	return (
		<>
			{vesselLastVoyagesData && vesselLastStoppageEventsData && vesselLastPortCallsData && vesselVoyageDailySignalsData && (
				<Suspense fallback={<LoadingSpinner size="lg" />}>
					<CsvDownloadButton
						data={
							(vesselVoyagesHourlyTracks &&
								vesselVoyagesHourlyTracks.dataList &&
								vesselVoyagesHourlyTracks.dataList.map((item) => ({
									time: item.signalDateTime,
									hoursSincePreviousSignal: formatValueAsString(item.hoursSincePreviousSignal, formatOptions, '', '', ''),
									latitude: item.latitude,
									longitude: item.longitude,
									heading: item.heading,
									speed: item.speed,
									operationalPhase: item.operationalPhase,
									generalSeaArea: item.generalSeaArea,
									loadedCondition: item.loadedCondition,
									percentDesignDraught: item.percentDesignDraught,
								}))) ||
							[]
						}
						filename={'voyages_hourly_track_' + props.imoNumber}
						reverseIconColor
						title="Download hourly track as CSV"
						dataLoading={vesselVoyagesHourlyTracks?.loading}
					/>
					<GlobeMap
						center={{
							longitude: vesselLastSignalInfo?.geometry?.coordinates?.[0] || 0,
							latitude: vesselLastSignalInfo?.geometry?.coordinates?.[1] || 0,
						}}
						zoomLevel={1.85}
					>
						<Source id="portCalls" type="geojson" data={vesselLastPortCallsData}>
							<Layer {...portCallsLayerStyle()} type="circle" />
						</Source>

						<Source id="lines" type="geojson" data={fixAntimeridian(vesselLastVoyagesData)}>
							<Layer {...lineLayerStyle()} />
						</Source>

						<Source id="voyageDailySignals" type="geojson" data={vesselVoyageDailySignalsData}>
							<Layer {...pointArrowLayerStyleForVoyage()} type="symbol" />
						</Source>

						<Source id="stoppages" type="geojson" data={vesselLastStoppageEventsData}>
							<Layer {...stoppagesLayerStyle} type="circle" />
						</Source>
					</GlobeMap>
				</Suspense>
			)}
		</>
	);
};
export default OperationalPhaseMap;
